import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Col, DatePicker, message } from "antd";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { OPENING_INVENTORY } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";
import TestComponentToPrint from "../../../components/specificComponents/TestCompToPrint";
import InvTransactionReportContent from "../../../components/specificComponents/InvTransactionReportContent";

const { RangePicker } = DatePicker;

const initialSearchValues = {
  SearchText: "",
  TransactionNo: "",
  ReferenceNo: "",
  BranchId: null,
  DepartmentId: null,
  TransactionDateFrom: null,
  TransactionDateTo: null,
  PageNumber: 1,
  PageSize: 10,
};

const columns = [
  {
    title: "Transaction No",
    dataIndex: "TransactionNo",
    key: "TransactionNo",
    width: 150,
  },
  {
    title: "Transaction Date",
    dataIndex: "TransactionDate",
    key: "TransactionDate",
    width: 150,
    render: (text) => {
      if (!text) return "";
      return text.split("T")[0];
    },
  },

  {
    title: "Branch",
    dataIndex: "BranchName",
    key: "BranchName",
    width: 150,
  },
  {
    title: "Department",
    dataIndex: "DepartmentName",
    key: "DepartmentName",
    width: 150,
  },
  {
    title: "Amount",
    dataIndex: "Amount",
    key: "Amount",
    width: 120,
    render: (text) => (text ? parseFloat(text).toFixed(2) : "0.00"),
  },
  {
    title: "Status",
    dataIndex: "IsSubmit",
    key: "IsSubmit",
    width: 100,
    render: (text) => (text ? "Post" : "Un-Post"),
  },
];

const OPENING_INVENTORY_REPORT_COLUMNS = [
  { key: "ItemName", label: "Item" },
  { key: "IssuanceUnit", label: "Unit" },
  { key: "QtyIU", label: "Qty", numeric: true },
  { key: "RateIU", label: "Rate", numeric: true },
  { key: "Amount", label: "Amount", numeric: true },
];

const OpeningInventory = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const componentRefPrint = useRef();
  const [reportData, setReportData] = useState(null);

  const [searchFields, setSearchFields] = useState(initialSearchValues);
  const [transactionList, setTransactionList] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [tableColumns] = useState([...columns]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, []);


  const fetchData = async (searchParams = {}, pageNumber = 1, pageSize = 10) => {
    setTableLoading(true);
    const payload = {
      OperationId: 1, // Get list operation
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: OPENING_INVENTORY,
      SearchText: searchParams.SearchText || "",
      TransactionNo: searchParams.TransactionNo || "",
      ReferenceNo: searchParams.ReferenceNo || "",
      BranchId: searchParams.BranchId || null,
      DepartmentId: searchParams.DepartmentId || null,
      TransactionDate: searchParams.TransactionDateFrom || null,
      PageNumber: pageNumber,
      PageSize: pageSize,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;
      setTransactionList(dataSet.Data || dataSet.DataSet?.Table || []);

      // Update pagination
      const totalCount =
        dataSet?.TotalCount ||
        dataSet?.TotalRecords ||
        dataSet?.Data?.length ||
        0;
      setPagination((prev) => ({
        ...prev,
        current: pageNumber,
        pageSize: pageSize,
        total: totalCount,
      }));

      // Set supporting tables if available
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || [],
          Departments:
            dataSet.AdditionalData?.Departments || [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const handleSearchChange = (data) => {
    setSearchFields((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleDateRangeChange = (dates) => {
    setSearchFields((prev) => ({
      ...prev,
      TransactionDateFrom: dates && dates[0] ? dates[0].format("YYYY-MM-DD") : null,
      TransactionDateTo: dates && dates[1] ? dates[1].format("YYYY-MM-DD") : null,
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(searchFields, 1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchFields(initialSearchValues);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(initialSearchValues, 1, pagination.pageSize);
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));

    fetchData(searchFields, paginationInfo.current, paginationInfo.pageSize);
  };

  const handleDeleteRow = (id, record) => {
    if (!checkFeatureAuth("DELETE", "OpeningInventory")) {
      return;
    }
    const payload = {
      OperationId: 4, // Delete operation
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: id,
    };

    postRequest("/Inv_Transaction", payload, controller)
      .then((response) => {
        if (response.error) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(
            response.data.DataSet?.Table?.errorMessage || "Error deleting transaction"
          );
          return;
        }
        message.success(response.successMessage || "Transaction deleted successfully");
        fetchData(searchFields, pagination.current, pagination.pageSize);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error deleting transaction:", error);
          message.error("Failed to delete transaction");
        }
      });
  };

  const handleEditRow = (setUpdateId) => {
    // This is handled by the component's navigation
    // The component will navigate to edit page
  };

  const viewReport = (record, handlePrint) => {
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: OPENING_INVENTORY,
      TransactionMasterId: record.TransactionMasterId,
      IsDetailData: true,
    };

    postRequest("/Inv_Transaction", payload, controller)
      .then((response) => {
        if (response.error) {
          message.error(response.errorMessage);
          return;
        }
        if (response.data.response === false) {
          message.error(
            response.data.DataSet?.Table?.errorMessage ||
              "Error fetching report data"
          );
          return;
        }

        const dataSet = response.data;
        const masterData = dataSet?.Data?.[0] || {};
        const details = masterData?.TransactionDetails || [];

        setReportData({
          master: masterData,
          details,
          record,
        });

        setTimeout(() => {
          handlePrint();
        }, 100);
      })
      .catch((error) => {
        if (error.message !== "canceled") {
          console.error("Error fetching report:", error);
          message.error("Failed to fetch report data");
        }
      });
  };

  const searchPanel = (
    <Fragment>
      {/* <FormTextField
        colSpan={6}
        label="Search Text"
        name="SearchText"
        size={INPUT_SIZE}
        value={searchFields.SearchText}
        onChange={handleSearchChange}
        placeholder="Search by transaction no, reference no..."
      /> */}

      <FormTextField
        colSpan={6}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={searchFields.TransactionNo}
        onChange={handleSearchChange}
      />

      {/* <FormTextField
        colSpan={4}
        label="Reference No"
        name="ReferenceNo"
        size={INPUT_SIZE}
        value={searchFields.ReferenceNo}
        onChange={handleSearchChange}
      /> */}

      <FormSelect
        colSpan={6}
        listItem={supportingTable.Branches || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={searchFields.BranchId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      <FormSelect
        colSpan={6}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={searchFields.DepartmentId}
        onChange={handleSearchChange}
        allowClear={true}
      />

      {/* <Col span={6}>
        <label style={{ display: "block", marginBottom: 5 }}>Transaction Date</label>
        <RangePicker
          style={{ width: "100%" }}
          value={
            searchFields.TransactionDateFrom && searchFields.TransactionDateTo
              ? [
                moment(searchFields.TransactionDateFrom),
                moment(searchFields.TransactionDateTo),
              ]
              : null
          }
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
        />
      </Col> */}

    </Fragment>
  );

  return (
    <Fragment>
    <BasicFormComponentInvTransaction
      formTitle="Opening Inventory"
      searchPanel={searchPanel}
      searchSubmit={handleSearchSubmit}
      tableColumn={tableColumns}
      tableRows={transactionList}
      tableLoading={tableLoading}
      actionID="TransactionMasterId"
      editRow={handleEditRow}
      deleteRow={handleDeleteRow}
      hideDelete={!checkFeatureAuth("DELETE", "OpeningInventory", { showErrorMessage: false })}
      baseRoute="/openingInventory"
      reset={false}
      handleReset={handleReset}
      paginationConfig={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
      }}
      onPaginationChange={handleTableChange}
      report={true}
      viewReport={viewReport}
      componentRefPrint={componentRefPrint}
    />
    <div style={{ display: "none" }}>
      <TestComponentToPrint
        ref={componentRefPrint}
        Bill={
          <InvTransactionReportContent
            title="Opening Inventory Detail Report"
            data={reportData}
            detailColumns={OPENING_INVENTORY_REPORT_COLUMNS}
            extraInfoFields={[{ key: "Amount", label: "Amount" }]}
          />
        }
      />
    </div>
  </Fragment>
  );
};

export default OpeningInventory;
